import React from 'react';
import './articles.css';
import ArticleLayout from './ArticleLayout';
import Footer from '../../../../components/Footer/Footer';
import { AssetUrls } from 'config/assets';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';

const ConditionGuide = () => {
  return (
    <>
      <SEOHelmet
        title="Card Condition Guide - TradeMagic"
        description="Learn how to evaluate Magic: The Gathering card conditions. Complete guide covering Near Mint, Lightly Played, Moderately Played, and Heavily Played grades."
        canonical="https://trademagic.gg/articles/condition-guide"
        keywords="MTG card condition, Magic card grading, Near Mint, Lightly Played, card condition guide"
        type="article"
      />

      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg' },
          { name: 'Articles', url: 'https://trademagic.gg/articles' },
          { name: 'Condition Guide', url: 'https://trademagic.gg/articles/condition-guide' },
        ]}
      />

      <ArticleLayout
        title="Condition Guide"
        hero={AssetUrls.articles.conditionGuide}
      >
        <section className="discount-section">
          <p>
            Card condition directly impacts value, and as a trader, it’s important to be mindful of your partner’s expectations.
            When in doubt, refer to this guide or send a polite message to clarify — clear communication leads to better trades for everyone.
          </p>
          <table className="discount-table">
            <thead>
              <tr>
                <th></th>
                <th>Near Mint</th>
                <th>Slightly Played</th>
                <th>Moderately Played</th>
                <th>Heavily Played</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Value</td>
                <td>100%</td>
                <td>85%</td>
                <td>75%</td>
                <td>50%</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section className="condition-examples">
          <div className="condition-row">
            <img
              src={AssetUrls.articles.conditionNM}
              alt="Near Mint Example"
            />
            <div className="condition-content">
              <h2>Near Mint</h2>
              <p>
                Cards in Near Mint (NM) condition have no printing imperfections or damage to the naked eye,
                or very minor printing imperfections or minimal wear from shuffling or general play visible only upon close inspection.
                Surfaces of cards in this condition should reflect clean gloss and be virtually free of scratches.
              </p>
              <p>
                Foils in this category have little or no scuffing or clouding on the front.
              </p>
              <p>
                This condition is so close to mint that many sites group the two conditions together and treat them the same.
              </p>
            </div>
          </div>

          <div className="condition-row">
            <img
              src={AssetUrls.articles.conditionLP}
              alt="Slightly Played Example"
            />
            <div className="condition-content">
              <h2>Slightly Played</h2>
              <p>
                Cards in SP condition have noticeable, but minor, imperfections or wear. Surfaces of cards in this condition
                should reflect mostly solid gloss but may have minor scratches.
              </p>
              <p>
                Cards in this condition may have minor border or corner wear, slight binder bending, printing imperfections,
                light scratching, clouding, or scuffing/loss of sheen on the front due to play wear.
              </p>
              <p>
                Cards marked with a pro-tour stamp or signed by the original artist are considered Slightly Played
                if they would otherwise be NM.
              </p>
              <p>
                Foils in this condition will generally have some clouding or wear on the front of the card.
                Sleeves may or may not be needed to play these cards in a tournament.
              </p>
            </div>
          </div>

          <div className="condition-row">
            <img
              src={AssetUrls.articles.conditionMP}
              alt="Moderately Played Example"
            />
            <div className="condition-content">
              <h2>Moderately Played</h2>
              <p>
                Cards in MP condition have noticeable imperfections or moderate wear on the card.
                Gloss on these cards may be lost with scuffing, and could have an extremely subtle tear.
              </p>
              <p>
                Cards in this condition may have border or corner wear, binder bending, printing imperfections,
                whitening on the back or front face of the card, slight inking along the back borders, scratching,
                clouding, or scuffing/loss of sheen on the front due to play wear.
              </p>
              <p>
                Foils in this condition will generally have clouding or wear on the front of the card.
              </p>
            </div>
          </div>

          <div className="condition-row">
            <img
              src={AssetUrls.articles.conditionHP}
              alt="Heavily Played Example"
            />
            <div className="condition-content">
              <h2>Heavily Played</h2>
              <p>
                Cards in HP condition show significant wear and damage from frequent play.
                These imperfections are obvious at a glance and may include major edge wear,
                surface scratches, creasing, inking, and heavy clouding.
              </p>
              <p>
                Cards in this condition may also exhibit warping, extensive whitening,
                or dirt and grime embedded in the surface. The gloss finish is typically dulled or missing in large areas.
              </p>
              <p>
                Foils in this category often have severe clouding or surface disruption.
                These cards are still playable in sleeves if the damage does not mark them.
                Most collectors do not consider HP cards suitable for collection.
              </p>
            </div>
          </div>
        </section>

        <section className="vocab-section">
          <h2>Condition Vocabulary</h2>
          <p>Here are some common terms used to grade cards.</p>

          <div className="vocab-grid">
            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-EdgeWhitening1')} alt="Edge Whitening" />
              <h4>Edge Whitening</h4>
              <p>Visible white scuffing or wear along the border, typically caused by handling or friction with sleeves and surfaces.</p>
            </div>

            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-Scratches')} alt="Scratches" />
              <h4>Scratches</h4>
              <p>Lines or marks on the card surface, usually from play or friction. Can be shallow or deep, and often reduce clarity or gloss.</p>
            </div>

            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-ShuffleCrease')} alt="Shuffle Crease" />
              <h4>Shuffle Crease</h4>
              <p>Fine creases—usually on corners or edges—caused by repeated shuffling. Often visible under light or close inspection.</p>
            </div>

            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-WaterDmg')} alt="Water Damage" />
              <h4>Water Damage</h4>
              <p>Warping, bubbling, or color bleeding caused by exposure to moisture. Severely affects structural integrity and value.</p>
            </div>

            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-Dirt')} alt="Dirt & Oil" />
              <h4>Dirt & Oil</h4>
              <p>Grime buildup from fingers or surfaces. Common in heavily played cards, usually seen as dark spots or smudging.</p>
            </div>

            <div className="vocab-card">
              <img src={AssetUrls.articles.byName('Condition-BinderDing')} alt="Binder Ding" />
              <h4>Binder Ding</h4>
              <p>A small circular dent typically caused by the rings of a binder. Often shows up as a depression without surface breakage.</p>
            </div>
          </div>
        </section>
      </ArticleLayout>

      <Footer />
    </>
  );
};

export default ConditionGuide;
